import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search, CheckSquare, X, Send, Plus, Edit2, Trash2,
  Eye, Users, MessageSquare, Clock, CheckCircle,
  Loader2, Tag, Settings,
  ArrowRight, BarChart3, Phone
} from 'lucide-react';
import AdminLayout from '../components/AdminLayout';
import { apiFetch } from '../lib/api';
import { usePermissions } from '../hooks/usePermissions';
import { PERMISSIONS } from '../lib/permissions';

interface CampaignTemplate {
  id: number;
  campaignId: number;
  versionLabel: string;
  messageContent: string;
  sortOrder: number;
}

interface Campaign {
  id: number;
  name: string;
  messageContent: string;
  mediaUrl?: string;
  mediaType?: string;
  createdAt: number;
  templates?: CampaignTemplate[];
}

interface Batch {
  id: number;
  campaignId: number;
  name: string;
  targetType?: string;
  targetCount: number;
  sentCount: number;
  failedCount: number;
  status: string;
  campaignName?: string;
  createdAt: number;
  scheduledAt?: number;
}

interface Customer {
  name: string;
  phone: string;
  estate: string;
  address?: string;
  lastOrderAt: number | string;
  totalOrders?: number;
  orderCount?: number;
  totalSpent: number;
  manualTags?: { id: number; tag: string; color: string }[];
  source?: 'order' | 'imported';
  isUnsubscribed?: boolean;
}

type TabId = 'create' | 'list' | 'templates';

const tabs = [
  { id: 'create' as TabId, label: '創建任務', icon: Send },
  { id: 'list' as TabId, label: '任務列表', icon: BarChart3 },
  { id: 'templates' as TabId, label: '訊息模板', icon: MessageSquare },
];

function replaceVars(template: string, customer: { name?: string }): string {
  return template.replace(/\{\{name\}\}/g, customer.name || '');
}

function highlightVars(text: string): React.ReactNode[] {
  const parts = text.split('{{name}}');
  const result: React.ReactNode[] = [];
  parts.forEach((part, i) => {
    result.push(<React.Fragment key={'p' + i}>{part}</React.Fragment>);
    if (i < parts.length - 1) {
      result.push(
        <span key={'v' + i} className="bg-orange-100 text-orange-700 px-1 rounded font-mono text-xs dark:bg-orange-900/30 dark:text-orange-300">
          {'{{name}}'}
        </span>
      );
    }
  });
  return result;
}

function formatDate(ts: number | string): string {
  if (!ts) return '-';
  const d = new Date(typeof ts === 'string' && ts.length > 10 ? ts : Number(ts) * 1000);
  if (isNaN(d.getTime())) return '-';
  return d.toLocaleDateString('zh-HK');
}

function formatDateTime(ts: number | string): string {
  if (!ts) return '-';
  const d = new Date(typeof ts === 'string' && ts.length > 10 ? ts : Number(ts) * 1000);
  if (isNaN(d.getTime())) return '-';
  return d.toLocaleString('zh-HK', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
}

function formatDateTimeInput(value: string): string {
  const d = new Date(value);
  if (isNaN(d.getTime())) return '-';
  return d.toLocaleString('zh-HK', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
}

function statusBadge(status: string) {
  if (status === 'completed') return <span className="inline-flex items-center px-2 py-0.5 bg-green-100 text-green-700 rounded text-xs dark:bg-green-900/20 dark:text-green-300"><CheckCircle className="w-3 h-3 mr-1" />完成</span>;
  if (status === 'sending') return <span className="inline-flex items-center px-2 py-0.5 bg-orange-100 text-orange-700 rounded text-xs dark:bg-orange-900/20 dark:text-orange-300"><Loader2 className="w-3 h-3 mr-1 animate-spin" />發送中</span>;
  if (status === 'pending') return <span className="inline-flex items-center px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs dark:bg-blue-900/20 dark:text-blue-300"><Clock className="w-3 h-3 mr-1" />待發送</span>;
  if (status === 'cancelled') return <span className="inline-flex items-center px-2 py-0.5 bg-gray-100 text-gray-700 rounded text-xs dark:bg-gray-700 dark:text-gray-300"><X className="w-3 h-3 mr-1" />已取消</span>;
  return <span className="inline-flex items-center px-2 py-0.5 bg-gray-100 text-gray-700 rounded text-xs dark:bg-gray-700 dark:text-gray-300">{status}</span>;
}

const AdminBroadcast: React.FC = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem('admin_token');
  const { can } = usePermissions();

  const AUTO_TAGS = [
    { key: 'new-customer', label: '新客戶' },
    { key: 'returning', label: '回頭客' },
    { key: 'big-order', label: '大單' },
  ];

  const [activeTab, setActiveTab] = useState<TabId>('create');

  // Shared data
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [batches, setBatches] = useState<Batch[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [availableTags, setAvailableTags] = useState<{ key: string; label: string }[]>(AUTO_TAGS);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Create task state
  const [step, setStep] = useState(1);
  const [targetType, setTargetType] = useState<'all' | 'tags' | 'customers'>('all');
  const [selectedTagKeys, setSelectedTagKeys] = useState<string[]>([]);
  const [selectedPhones, setSelectedPhones] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState('');
  const [manualPhoneInput, setManualPhoneInput] = useState('');

  const [selectedCampaignId, setSelectedCampaignId] = useState<number | null>(null);
  const [, setTemplateContent] = useState('');
  const [batchName, setBatchName] = useState('');
  const [scheduledAt, setScheduledAt] = useState('');
  const [hourlyCap, setHourlyCap] = useState(100);
  const [minInterval, setMinInterval] = useState(30);
  const [maxInterval, setMaxInterval] = useState(60);

  const [previewData, setPreviewData] = useState<any>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [testPhone, setTestPhone] = useState('');
  const [testResult, setTestResult] = useState<any>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [isTestSending, setIsTestSending] = useState(false);
  const [isSavingCampaign, setIsSavingCampaign] = useState(false);



  // Templates state
  const [showCampaignForm, setShowCampaignForm] = useState(false);
  const [editingCampaignId, setEditingCampaignId] = useState<number | null>(null);
  const [campaignForm, setCampaignForm] = useState<{
    name: string;
    mediaUrl: string;
    mediaType: string;
    templates: { id?: number; versionLabel: string; messageContent: string }[];
  }>({ name: '', mediaUrl: '', mediaType: '', templates: [{ versionLabel: 'A', messageContent: '' }] });
  const [previewCampaign, setPreviewCampaign] = useState<Campaign | null>(null);

  // List state
  const [listStatusFilter, setListStatusFilter] = useState('all');
  const [listPage, setListPage] = useState(0);
  const [listTotal, setListTotal] = useState(0);
  const LIST_LIMIT = 20;

  useEffect(() => {
    if (!token) { navigate('/admin'); return; }
    fetchCampaigns();
    fetchBatches();
    fetchCustomers();
    fetchTags();
  }, []);

  useEffect(() => {
    if (activeTab === 'list') fetchBatches();
  }, [activeTab, listStatusFilter, listPage]);

  const fetchCampaigns = async () => {
    try {
      const res = await apiFetch('/api/public/admin/broadcast-campaigns');
      if (res.ok) { const data = await res.json(); setCampaigns(Array.isArray(data) ? data : []); }
    } catch (e) { console.error(e); }
  };

  const fetchBatches = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('limit', String(LIST_LIMIT));
      params.set('offset', String(listPage * LIST_LIMIT));
      if (listStatusFilter !== 'all') params.set('status', listStatusFilter);
      const res = await apiFetch('/api/public/admin/broadcast-batches?' + params.toString());
      if (res.ok) {
        const data = await res.json();
        setBatches(data.batches || []);
        setListTotal(data.total || 0);
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const fetchCustomers = async () => {
    try {
      const res = await apiFetch('/api/public/admin/customers');
      if (res.ok) { const data = await res.json(); setCustomers(Array.isArray(data) ? data : []); }
    } catch (e) { console.error(e); }
  };

  const fetchTags = async () => {
    try {
      const res = await apiFetch('/api/public/admin/customer-tags/all');
      let manualTags: string[] = [];
      if (res.ok) {
        const data = await res.json();
        manualTags = Array.isArray(data) ? data : [];
      } else {
        console.warn('[Broadcast] Failed to fetch manual tags, using auto tags only');
      }
      // Include auto tags (displayed with Chinese labels) plus manual tags
      setAvailableTags([
        ...AUTO_TAGS,
        ...manualTags
          .filter((t: string) => !AUTO_TAGS.some(a => a.key === t))
          .map((t: string) => ({ key: t, label: t }))
      ]);
    } catch (e) {
      console.error(e);
      // Always keep auto tags visible even if the API fails
      setAvailableTags([...AUTO_TAGS]);
    }
  };

  // --- Target selection helpers ---
  const filteredCustomers = useMemo(() => {
    if (!search.trim()) return customers;
    const q = search.trim().toLowerCase();
    return customers.filter(c => c.name.toLowerCase().includes(q) || c.phone.includes(q));
  }, [customers, search]);

  const toggleSelect = (phone: string) => {
    setSelectedPhones(prev => {
      const next = new Set(prev);
      if (next.has(phone)) next.delete(phone); else next.add(phone);
      return next;
    });
  };

  const selectAllFiltered = () => {
    setSelectedPhones(prev => {
      const next = new Set(prev);
      filteredCustomers.forEach(c => next.add(c.phone));
      return next;
    });
  };

  const clearSelection = () => setSelectedPhones(new Set());

  const parseManualPhones = () => {
    const lines = manualPhoneInput.split(/\n|,|\r/).map(l => l.trim()).filter(l => l);
    const phones: string[] = [];
    for (const line of lines) {
      // Support formats: "phone", "name,phone", "name phone", "phone name"
      const parts = line.split(/[,	]/).map(p => p.trim()).filter(p => p);
      if (parts.length >= 2) {
        // Try to find the phone part (contains mostly digits)
        const phonePart = parts.find(p => /^\+?\d{8,15}$/.test(p.replace(/\s/g, '')));
        if (phonePart) phones.push(phonePart.replace(/\s/g, ''));
      } else if (parts.length === 1) {
        const clean = parts[0].replace(/\D/g, '');
        if (clean.length >= 8) phones.push(clean);
      }
    }
    // Deduplicate
    const unique = Array.from(new Set(phones));
    return unique;
  };

  const addManualPhones = () => {
    const phones = parseManualPhones();
    if (phones.length === 0) { setError('沒有解析到有效的電話號碼'); return; }
    setSelectedPhones(prev => {
      const next = new Set(prev);
      phones.forEach(p => next.add(p));
      return next;
    });
    setManualPhoneInput('');
    setSuccess(`已添加 ${phones.length} 個電話號碼`);
    setTimeout(() => setSuccess(null), 3000);
  };

  // --- Campaign handlers ---
  const handleSaveCampaign = async () => {
    if (!campaignForm.name.trim()) {
      setError('請填寫模板名稱'); return;
    }
    const validTemplates = campaignForm.templates.filter(t => t.messageContent.trim());
    if (validTemplates.length === 0) {
      setError('至少需要一個訊息版本'); return;
    }
    if (validTemplates.length > 5) {
      setError('最多 5 個訊息版本'); return;
    }
    const method = editingCampaignId ? 'PUT' : 'POST';
    const url = editingCampaignId ? '/api/public/admin/broadcast-campaigns/' + editingCampaignId : '/api/public/admin/broadcast-campaigns';
    const payload = {
      name: campaignForm.name,
      mediaUrl: campaignForm.mediaUrl,
      mediaType: campaignForm.mediaType,
      templates: validTemplates.map((t, i) => ({
        id: t.id,
        versionLabel: t.versionLabel || String.fromCharCode(65 + i),
        messageContent: t.messageContent.trim(),
      })),
    };
    setIsSavingCampaign(true);
    try {
      const res = await apiFetch(url, { method, body: JSON.stringify(payload) });
      if (res.ok) {
        setSuccess(editingCampaignId ? '模板更新成功' : '模板創建成功');
        setShowCampaignForm(false);
        setCampaignForm({ name: '', mediaUrl: '', mediaType: '', templates: [{ versionLabel: 'A', messageContent: '' }] });
        fetchCampaigns();
        setTimeout(() => setSuccess(null), 3000);
      } else {
        const data = await res.json().catch(() => ({}));
        setError(data.error || '儲存模板失敗');
      }
    } catch (e: any) {
      setError(e.message || '儲存模板失敗');
    } finally {
      setIsSavingCampaign(false);
    }
  };

  const handleDeleteCampaign = async (id: number) => {
    if (!confirm('確定要刪除此模板嗎？')) return;
    const res = await apiFetch('/api/public/admin/broadcast-campaigns/' + id, { method: 'DELETE' });
    if (res.ok) { setSuccess('模板刪除成功'); fetchCampaigns(); setTimeout(() => setSuccess(null), 3000); }
    else { setError('刪除模板失敗'); }
  };

  // --- Preview ---
  const getScheduledTimestamp = (): number | undefined => {
    if (!scheduledAt) return undefined;
    const d = new Date(scheduledAt);
    return isNaN(d.getTime()) ? undefined : Math.floor(d.getTime() / 1000);
  };

  const handlePreview = async () => {
    setError(null);
    if (!selectedCampaignId) { setError('請選擇訊息模板'); return; }

    const body: any = {
      campaignId: selectedCampaignId,
      targetType,
      hourlyCap, minIntervalSeconds: minInterval, maxIntervalSeconds: maxInterval,
    };
    if (targetType === 'tags') body.targetTags = selectedTagKeys;
    if (targetType === 'customers') {
      body.phones = Array.from(selectedPhones);
      body.names = body.phones.map((p: string) => customers.find(c => c.phone === p)?.name || '');
    }

    setIsPreviewLoading(true);
    try {
      const res = await apiFetch('/api/public/admin/broadcast-batches?preview=true', {
        method: 'POST', body: JSON.stringify(body)
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || '預覽失敗'); return; }
      setPreviewData(data);
      setShowPreview(true);
    } catch (e: any) {
      setError(e.message || '預覽失敗');
    } finally {
      setIsPreviewLoading(false);
    }
  };

  // --- Test send ---
  const handleTestSend = async () => {
    if (!testPhone.trim()) { setError('請輸入測試手機號'); return; }
    if (!selectedCampaignId) { setError('請先選擇訊息模板'); return; }
    const selectedCampaign = campaigns.find(c => c.id === selectedCampaignId);
    const testTemplateContent = selectedCampaign?.templates?.[0]?.messageContent || selectedCampaign?.messageContent || '';
    if (!testTemplateContent.trim()) { setError('所选模板沒有訊息內容'); return; }
    setIsTestSending(true);
    try {
      const res = await apiFetch('/api/public/admin/broadcast-test', {
        method: 'POST',
        body: JSON.stringify({ phone: testPhone.trim(), templateContent: testTemplateContent, mediaUrl: selectedCampaign?.mediaUrl, mediaType: selectedCampaign?.mediaType })
      });
      const data = await res.json();
      setTestResult(data);
      if (data.success) setSuccess('測試發送成功！');
      else setError(data.error || '測試發送失敗');
    } catch (e: any) {
      setError(e.message || '測試發送失敗');
    } finally {
      setIsTestSending(false);
    }
  };

  // --- Create task ---
  const handleCreateTask = async () => {
    if (!batchName.trim()) { setError('請輸入任務名稱'); return; }
    if (!selectedCampaignId) { setError('請選擇訊息模板'); return; }

    const selectedCampaign = campaigns.find(c => c.id === selectedCampaignId);
    const body: any = {
      name: batchName.trim(),
      campaignId: selectedCampaignId,
      targetType,
      mediaUrl: selectedCampaign?.mediaUrl,
      mediaType: selectedCampaign?.mediaType,
      hourlyCap,
      minIntervalSeconds: minInterval,
      maxIntervalSeconds: maxInterval,
      scheduledAt: getScheduledTimestamp(),
    };
    if (targetType === 'tags') body.targetTags = selectedTagKeys;
    if (targetType === 'customers') {
      body.phones = Array.from(selectedPhones);
      body.names = body.phones.map((p: string) => customers.find(c => c.phone === p)?.name || '');
    }

    setIsSubmitting(true);
    try {
      const res = await apiFetch('/api/public/admin/broadcast-batches', {
        method: 'POST', body: JSON.stringify(body)
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || '創建任務失敗'); setIsSubmitting(false); return; }
      setSuccess(`任務創建成功！共 ${data.targetCount || 0} 位收件人，使用 ${data.versionCount || 1} 個訊息版本隨機發送。`);
      setShowPreview(false);
      setPreviewData(null);
      setBatchName('');
      setTemplateContent('');
      setSelectedCampaignId(null);
      setScheduledAt('');
      setStep(1);
      setActiveTab('list');
      fetchBatches();
      setTimeout(() => setSuccess(null), 3000);
    } catch (e: any) {
      setError(e.message || '創建任務失敗');
    }
    setIsSubmitting(false);
  };

  const handleLogout = () => {
    if (confirm('確定要登出嗎？')) {
      localStorage.removeItem('admin_token');
      navigate('/admin');
    }
  };

  // Target display
  const targetTypeLabel: Record<string, string> = {
    all: '全部客戶', tags: '指定標籤', imported: '導入聯繫人', customers: '手動選擇'
  };

  return (
    <AdminLayout currentPage="broadcast" onLogout={handleLogout}>
      <div>
        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 flex justify-between items-center dark:bg-red-900/20 dark:border-red-800 dark:text-red-300">
            <span>{error}</span>
            <button onClick={() => setError(null)} className="text-red-700 hover:text-red-900 dark:text-red-300 dark:hover:text-red-100"><X className="w-5 h-5" /></button>
          </div>
        )}
        {success && (
          <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg text-green-700 flex justify-between items-center dark:bg-green-900/20 dark:border-green-800 dark:text-green-300">
            <span>{success}</span>
            <button onClick={() => setSuccess(null)} className="text-green-700 hover:text-green-900 dark:text-green-300 dark:hover:text-green-100"><X className="w-5 h-5" /></button>
          </div>
        )}

        {isSubmitting && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6 flex flex-col items-center shadow-lg mx-4">
              <Loader2 className="w-8 h-8 animate-spin text-orange-600 mb-3" />
              <p className="text-gray-800 dark:text-white text-center">
                {isSubmitting ? '正在創建廣播任務，請稍候...' : '正在導入聯繫人，請稍候...'}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">處理大量數據時可能需要數秒至數十秒</p>
            </div>
          </div>
        )}

        <div className="bg-white dark:bg-gray-800 border-b dark:border-gray-700 mb-6 rounded-t-lg">
          <nav className="flex space-x-2 px-2 md:space-x-8 md:px-6 overflow-x-auto" aria-label="分頁">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={'py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap flex items-center gap-1 ' + (
                  activeTab === tab.id
                    ? 'border-orange-500 text-orange-600 dark:text-orange-400'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-500'
                )}
              >
                <tab.icon className="w-4 h-4" /> {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {activeTab === 'create' && (
          <div className="space-y-6">
            {/* Step indicator */}
            <div className="flex items-center gap-2 text-sm mb-4">
              {[1,2,3].map(s => (
                <React.Fragment key={s}>
                  <div className={`flex items-center justify-center w-8 h-8 rounded-full font-bold text-sm ${
                    step >= s ? 'bg-orange-600 text-white' : 'bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                  }`}>{s}</div>
                  <span className={step >= s ? 'text-orange-600 dark:text-orange-400 font-medium' : 'text-gray-500 dark:text-gray-400'}>
                    {s === 1 ? '選擇目標' : s === 2 ? '選擇模板' : '發送設置'}
                  </span>
                  {s < 3 && <div className="w-4 h-px bg-gray-300 dark:bg-gray-600" />}
                </React.Fragment>
              ))}
            </div>

            {/* Step 1: Target */}
            {step === 1 && (
              <div className="space-y-4">
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                  <h3 className="text-lg font-bold text-gray-800 dark:text-white mb-4 flex items-center gap-2">
                    <Users className="w-5 h-5 text-orange-600" /> 選擇發送目標
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-6">
                    {(['all', 'tags', 'customers'] as const).map(t => (
                      <button
                        key={t}
                        onClick={() => setTargetType(t)}
                        className={`p-4 rounded-lg border-2 text-left transition ${
                          targetType === t
                            ? 'border-orange-500 bg-orange-50 dark:bg-orange-900/20'
                            : 'border-gray-200 dark:border-gray-700 hover:border-orange-300'
                        }`}
                      >
                        <div className="font-medium text-gray-900 dark:text-white mb-1">{targetTypeLabel[t]}</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {t === 'all' ? `${customers.length} 位客戶` :
                           t === 'tags' ? '按標籤篩選' :
                           '手動勾選'}
                        </div>
                      </button>
                    ))}
                  </div>

                  {targetType === 'tags' && (
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">選擇標籤</label>
                      <div className="flex flex-wrap gap-2">
                        {availableTags.length === 0 ? (
                          <span className="text-sm text-gray-500 dark:text-gray-400">暫無標籤</span>
                        ) : (
                          availableTags.map(tag => (
                            <button
                              key={tag.key}
                              onClick={() => setSelectedTagKeys(prev => prev.includes(tag.key) ? prev.filter(t => t !== tag.key) : [...prev, tag.key])}
                              className={`px-3 py-1 rounded-full text-sm border ${
                                selectedTagKeys.includes(tag.key)
                                  ? 'bg-orange-600 text-white border-orange-600'
                                  : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600'
                              }`}
                            >
                              {tag.label}
                            </button>
                          ))
                        )}
                      </div>
                    </div>
                  )}

                  {targetType === 'customers' && (
                    <div className="space-y-3">
                      {/* Manual phone input */}
                      <div className="p-4 bg-orange-50 dark:bg-orange-900/10 border border-orange-200 dark:border-orange-800 rounded-lg">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          <Phone className="w-4 h-4 inline mr-1 text-orange-600" />
                          手動輸入電話號碼（每行一個，或逗號分隔）
                        </label>
                        <textarea
                          rows={3}
                          value={manualPhoneInput}
                          onChange={e => setManualPhoneInput(e.target.value)}
                          placeholder="85291234567&#10;85292345678&#10;陳大文, 85293456789"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 outline-none dark:bg-gray-800 dark:text-white dark:border-gray-600 text-sm font-mono"
                        />
                        <div className="flex items-center justify-between mt-2">
                          <span className="text-xs text-gray-500 dark:text-gray-400">支持格式：純電話號碼 / 姓名,電話號碼</span>
                          <button
                            onClick={addManualPhones}
                            disabled={!manualPhoneInput.trim()}
                            className="flex items-center px-3 py-1.5 bg-orange-600 text-white rounded text-sm hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <Plus className="w-4 h-4 mr-1" /> 添加到已選
                          </button>
                        </div>
                      </div>

                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                        <input
                          type="text" placeholder="搜索客戶名或電話..."
                          value={search} onChange={e => setSearch(e.target.value)}
                          className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 outline-none dark:bg-gray-800 dark:text-white dark:border-gray-600"
                        />
                      </div>
                      <div className="flex gap-2">
                        <button onClick={selectAllFiltered} className="flex items-center px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600">
                          <CheckSquare className="w-4 h-4 mr-1" /> 全選
                        </button>
                        <button onClick={clearSelection} className="flex items-center px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600">
                          <X className="w-4 h-4 mr-1" /> 清除
                        </button>
                      </div>
                      <div className="text-sm text-gray-600 dark:text-gray-400">
                        已選擇 <span className="font-bold text-orange-600 dark:text-orange-400">{selectedPhones.size}</span> 人
                        {selectedPhones.size > 0 && (
                          <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">
                            （{Array.from(selectedPhones).slice(0, 5).join(', ')}{selectedPhones.size > 5 ? ` ...等${selectedPhones.size}個` : ''}）
                          </span>
                        )}
                      </div>
                      <div className="max-h-64 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg">
                        <table className="w-full text-sm text-left">
                          <thead className="bg-gray-50 dark:bg-gray-700 text-gray-700 dark:text-gray-300 uppercase text-xs">
                            <tr>
                              <th className="px-3 py-2 w-8"><input type="checkbox" checked={filteredCustomers.length > 0 && filteredCustomers.every(c => selectedPhones.has(c.phone))} onChange={e => e.target.checked ? selectAllFiltered() : clearSelection()} className="w-4 h-4 text-orange-600 rounded" /></th>
                              <th className="px-3 py-2">客戶</th><th className="px-3 py-2">電話</th><th className="px-3 py-2">屋苑</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                            {filteredCustomers.map(c => (
                              <tr key={c.phone} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                                <td className="px-3 py-2"><input type="checkbox" checked={selectedPhones.has(c.phone)} onChange={() => toggleSelect(c.phone)} className="w-4 h-4 text-orange-600 rounded" /></td>
                                <td className="px-3 py-2 font-medium text-gray-900 dark:text-white">{c.name || '-'}</td>
                                <td className="px-3 py-2 text-gray-600 dark:text-gray-300">{c.phone}</td>
                                <td className="px-3 py-2 text-gray-600 dark:text-gray-300">{c.estate || '-'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  <div className="mt-4 flex justify-end">
                    <button
                      onClick={() => {
                        if (targetType === 'tags' && selectedTagKeys.length === 0) { setError('請選擇至少一個標籤'); return; }
                        if (targetType === 'customers' && selectedPhones.size === 0) { setError('請選擇至少一位客戶'); return; }
                        setError(null); setStep(2);
                      }}
                      className="flex items-center px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700"
                    >
                      下一步 <ArrowRight className="w-4 h-4 ml-1" />
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Step 2: Template */}
            {step === 2 && (
              <div className="space-y-4">
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                  <h3 className="text-lg font-bold text-gray-800 dark:text-white mb-4 flex items-center gap-2">
                    <MessageSquare className="w-5 h-5 text-orange-600" /> 選擇訊息模板
                  </h3>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">選擇模板</label>
                    <select
                      value={selectedCampaignId ?? ''}
                      onChange={e => {
                        const id = Number(e.target.value);
                        setSelectedCampaignId(id || null);
                        const campaign = campaigns.find(c => c.id === id);
                        setTemplateContent(campaign?.templates?.[0]?.messageContent || campaign?.messageContent || '');
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg dark:bg-gray-800 dark:text-white dark:border-gray-600"
                    >
                      <option value="">請選擇...</option>
                      {campaigns.map(c => <option key={c.id} value={c.id}>{c.name}（{c.templates?.length || 1} 個版本）</option>)}
                    </select>
                  </div>

                  {selectedCampaignId && (
                    <div className="space-y-3">
                      <div className="p-3 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg">
                        <p className="text-sm text-orange-800 dark:text-orange-300">
                          此模板有 <span className="font-bold">{campaigns.find(c => c.id === selectedCampaignId)?.templates?.length || 1}</span> 個訊息版本，系統會為每位收件人隨機挑選一個版本發送，降低被視為機械發送的風險。
                        </p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">版本預覽</label>
                        <div className="space-y-2 max-h-64 overflow-y-auto">
                          {(campaigns.find(c => c.id === selectedCampaignId)?.templates || []).map((t, i) => (
                            <div key={t.id || i} className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="px-2 py-0.5 bg-orange-100 text-orange-700 text-xs rounded font-medium dark:bg-orange-900/30 dark:text-orange-300">版本 {t.versionLabel || String.fromCharCode(65 + i)}</span>
                              </div>
                              <p className="text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap break-words">{highlightVars(t.messageContent)}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="mt-4 flex justify-between">
                    <button onClick={() => setStep(1)} className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-white rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600">上一步</button>
                    <button
                      onClick={() => { if (!selectedCampaignId) { setError('請選擇訊息模板'); return; } setError(null); setStep(3); }}
                      className="flex items-center px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700"
                    >
                      下一步 <ArrowRight className="w-4 h-4 ml-1" />
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Step 3: Settings */}
            {step === 3 && (
              <div className="space-y-4">
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                  <h3 className="text-lg font-bold text-gray-800 dark:text-white mb-4 flex items-center gap-2">
                    <Settings className="w-5 h-5 text-orange-600" /> 發送設置
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">任務名稱</label>
                      <input type="text" value={batchName} onChange={e => setBatchName(e.target.value)} placeholder="例如：週末推廣" className="w-full px-3 py-2 border border-gray-300 rounded-lg dark:bg-gray-800 dark:text-white dark:border-gray-600" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">定時發送（可選）</label>
                      <input type="datetime-local" value={scheduledAt} onChange={e => setScheduledAt(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg dark:bg-gray-800 dark:text-white dark:border-gray-600" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">每小時上限（則）</label>
                      <input type="number" min={1} max={500} value={hourlyCap} onChange={e => setHourlyCap(Number(e.target.value))} className="w-full px-3 py-2 border border-gray-300 rounded-lg dark:bg-gray-800 dark:text-white dark:border-gray-600" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">發送間隔（秒）</label>
                      <div className="flex items-center gap-2">
                        <input type="number" min={5} max={300} value={minInterval} onChange={e => setMinInterval(Math.min(Number(e.target.value), maxInterval))} className="w-20 px-3 py-2 border border-gray-300 rounded-lg dark:bg-gray-800 dark:text-white dark:border-gray-600" />
                        <span className="text-gray-500 dark:text-gray-400">-</span>
                        <input type="number" min={5} max={300} value={maxInterval} onChange={e => setMaxInterval(Math.max(Number(e.target.value), minInterval))} className="w-20 px-3 py-2 border border-gray-300 rounded-lg dark:bg-gray-800 dark:text-white dark:border-gray-600" />
                        <span className="text-xs text-gray-500 dark:text-gray-400">隨機間隔</span>
                      </div>
                    </div>
                  </div>

                  {can(PERMISSIONS.BROADCAST_SEND) && (
                    <>
                      {/* Test send */}
                      <div className="mt-6 p-4 bg-gray-50 dark:bg-gray-700/30 rounded-lg">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">測試發送</label>
                        <div className="flex gap-2">
                          <input type="text" value={testPhone} onChange={e => setTestPhone(e.target.value)} placeholder="輸入測試手機號碼" className="flex-1 px-3 py-2 border border-gray-300 rounded-lg dark:bg-gray-800 dark:text-white dark:border-gray-600" />
                          <button
                            onClick={handleTestSend}
                            disabled={isTestSending}
                            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {isTestSending ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
                            發送測試
                          </button>
                        </div>
                        {testResult && (
                          <div className={`mt-2 p-2 rounded text-sm ${testResult.success ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300' : 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-300'}`}>
                            {testResult.success ? '測試發送成功' : `測試發送失敗：${testResult.error || '未知錯誤'}`}
                          </div>
                        )}
                      </div>
                    </>
                  )}

                  <div className="mt-4 flex justify-between">
                    <button onClick={() => setStep(2)} className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-white rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600">上一步</button>
                    <button
                      onClick={() => { if (!batchName.trim()) { setError('請輸入任務名稱'); return; } setError(null); handlePreview(); }}
                      disabled={isPreviewLoading}
                      className="flex items-center px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isPreviewLoading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Eye className="w-4 h-4 mr-1" />}
                      預覽任務
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'list' && (
          <div className="space-y-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 flex flex-wrap items-center gap-3">
              <select value={listStatusFilter} onChange={e => { setListStatusFilter(e.target.value); setListPage(0); }} className="px-3 py-2 border border-gray-300 rounded-lg dark:bg-gray-800 dark:text-white dark:border-gray-600">
                <option value="all">全部狀態</option>
                <option value="pending">待發送</option>
                <option value="sending">發送中</option>
                <option value="completed">已完成</option>
                <option value="cancelled">已取消</option>
              </select>
              <span className="text-sm text-gray-600 dark:text-gray-400">共 {listTotal} 個任務</span>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
              {loading ? (
                <div className="p-8 text-center text-gray-500 dark:text-gray-400">加載中...</div>
              ) : batches.length === 0 ? (
                <div className="p-8 text-center text-gray-500 dark:text-gray-400">暫無任務</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm text-left">
                    <thead className="bg-gray-50 dark:bg-gray-700 text-gray-700 dark:text-gray-300 uppercase text-xs">
                      <tr>
                        <th className="px-4 py-3">任務名稱</th>
                        <th className="px-4 py-3">目標類型</th>
                        <th className="px-4 py-3">進度</th>
                        <th className="px-4 py-3">狀態</th>
                        <th className="px-4 py-3">預計發送</th>
                        <th className="px-4 py-3">創建時間</th>
                        <th className="px-4 py-3"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                      {batches.map(batch => {
                        const total = batch.targetCount || 0;
                        const done = (batch.sentCount || 0) + (batch.failedCount || 0);
                        const pct = total > 0 ? Math.round((done / total) * 100) : 0;
                        return (
                          <tr key={batch.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                            <td className="px-4 py-3">
                              <div className="font-medium text-gray-900 dark:text-white">{batch.name}</div>
                              {batch.campaignName && <div className="text-xs text-gray-500 dark:text-gray-400">{batch.campaignName}</div>}
                            </td>
                            <td className="px-4 py-3 text-gray-600 dark:text-gray-300">{targetTypeLabel[batch.targetType || ''] || batch.targetType || '-'}</td>
                            <td className="px-4 py-3">
                              <div className="w-24">
                                <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mb-1"><span>{done}/{total}</span><span>{pct}%</span></div>
                                <div className="w-full bg-gray-200 rounded-full h-1.5 dark:bg-gray-700">
                                  <div className="bg-orange-600 h-1.5 rounded-full" style={{ width: `${pct}%` }} />
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-3">{statusBadge(batch.status)}</td>
                            <td className="px-4 py-3 text-gray-600 dark:text-gray-300 whitespace-nowrap">
                              {batch.status === 'pending' && batch.scheduledAt
                                ? formatDateTime(batch.scheduledAt)
                                : '-'}
                            </td>
                            <td className="px-4 py-3 text-gray-600 dark:text-gray-300">{formatDate(batch.createdAt)}</td>
                            <td className="px-4 py-3">
                              <button onClick={() => navigate(`/admin/broadcast/${batch.id}`)} className="text-orange-600 hover:text-orange-700 text-sm">查看詳情</button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
              {listTotal > LIST_LIMIT && (
                <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex justify-center gap-2">
                  <button disabled={listPage === 0} onClick={() => setListPage(p => p - 1)} className="px-3 py-1 bg-gray-100 dark:bg-gray-700 rounded text-sm disabled:opacity-50">上一頁</button>
                  <span className="px-3 py-1 text-sm text-gray-600 dark:text-gray-400">第 {listPage + 1} 頁</span>
                  <button disabled={(listPage + 1) * LIST_LIMIT >= listTotal} onClick={() => setListPage(p => p + 1)} className="px-3 py-1 bg-gray-100 dark:bg-gray-700 rounded text-sm disabled:opacity-50">下一頁</button>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'templates' && (
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              {can(PERMISSIONS.BROADCAST_SEND) && (
                <button onClick={() => { setEditingCampaignId(null); setCampaignForm({ name: '', mediaUrl: '', mediaType: '', templates: [{ versionLabel: 'A', messageContent: '' }] }); setShowCampaignForm(true); }} className="flex items-center px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700">
                  <Plus className="w-5 h-5 mr-2" /> 新增模板
                </button>
              )}
            </div>
            {campaigns.length === 0 ? (
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-8 text-center text-gray-500 dark:text-gray-400">暫無模板</div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {campaigns.map(campaign => (
                  <div key={campaign.id} className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 border-l-4 border-orange-500 flex flex-col">
                    <div className="mb-3 flex items-start gap-2">
                      <Tag className="w-5 h-5 text-orange-500 mt-0.5 flex-shrink-0" />
                      <h3 className="text-lg font-bold text-gray-900 dark:text-white">{campaign.name}</h3>
                    </div>
                    <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3 mb-4 flex-1">
                      <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap break-words line-clamp-6">{campaign.messageContent}</p>
                      {campaign.mediaUrl && (
                        <div className="mt-2 flex items-center gap-2 text-xs text-orange-600 dark:text-orange-400">
                          <span className="inline-flex items-center px-2 py-0.5 bg-orange-100 dark:bg-orange-900/30 rounded">{campaign.mediaType || 'media'}</span>
                          <span className="truncate max-w-[200px]">{campaign.mediaUrl}</span>
                        </div>
                      )}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 mb-4">
                      創建於 {formatDate(campaign.createdAt)} · {(campaign.templates?.length || 1)} 個版本
                    </div>
                    <div className="flex gap-2">
                      <button onClick={() => setPreviewCampaign(campaign)} className="flex-1 flex items-center justify-center px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm">
                        <Eye className="w-4 h-4 mr-1" /> 預覽
                      </button>
                      {can(PERMISSIONS.BROADCAST_SEND) && (
                        <>
                          <button onClick={() => {
                            setEditingCampaignId(campaign.id);
                            const templates = campaign.templates?.length
                              ? campaign.templates.map(t => ({ id: t.id, versionLabel: t.versionLabel, messageContent: t.messageContent }))
                              : [{ versionLabel: 'A', messageContent: campaign.messageContent }];
                            setCampaignForm({ name: campaign.name, mediaUrl: campaign.mediaUrl || '', mediaType: campaign.mediaType || '', templates });
                            setShowCampaignForm(true);
                          }} className="flex-1 flex items-center justify-center px-3 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 text-sm">
                            <Edit2 className="w-4 h-4 mr-1" /> 編輯
                          </button>
                          <button onClick={() => handleDeleteCampaign(campaign.id)} className="flex-1 flex items-center justify-center px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm">
                            <Trash2 className="w-4 h-4 mr-1" /> 刪除
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Preview Modal */}
        {showPreview && previewData && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
              <h2 className="text-xl font-bold mb-4 text-gray-900 dark:text-white">任務預覽</h2>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between"><span className="text-gray-500 dark:text-gray-400">任務名稱：</span><span className="text-gray-900 dark:text-white font-medium">{batchName}</span></div>
                <div className="flex justify-between"><span className="text-gray-500 dark:text-gray-400">目標類型：</span><span className="text-gray-900 dark:text-white">{targetTypeLabel[targetType]}</span></div>
                <div className="flex justify-between"><span className="text-gray-500 dark:text-gray-400">預計收件人：</span><span className="text-orange-600 dark:text-orange-400 font-bold">{previewData.targetCount} 人</span></div>
                <div className="flex justify-between"><span className="text-gray-500 dark:text-gray-400">訊息版本：</span><span className="text-gray-900 dark:text-white">{previewData.versionCount || 1} 個（隨機發送）</span></div>
                <div className="flex justify-between"><span className="text-gray-500 dark:text-gray-400">預計耗時：</span><span className="text-gray-900 dark:text-white">約 {previewData.estimatedDurationMinutes} 分鐘</span></div>
                {scheduledAt && <div className="flex justify-between"><span className="text-gray-500 dark:text-gray-400">定時發送：</span><span className="text-gray-900 dark:text-white">{formatDateTimeInput(scheduledAt)}</span></div>}
              </div>
              {previewData.sampleRecipients?.length > 0 && (
                <div className="mt-4">
                  <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">樣本預覽：</p>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {previewData.sampleRecipients.map((r: any, i: number) => (
                      <div key={i} className="p-2 bg-gray-50 dark:bg-gray-700/50 rounded text-sm">
                        <div className="text-xs text-gray-500 dark:text-gray-400">{r.name || r.phone}</div>
                        <div className="text-gray-800 dark:text-gray-200 whitespace-pre-wrap break-words">{r.preview}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div className="mt-4 p-3 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg text-sm text-orange-800 dark:text-orange-300">
                確認創建任務後，系統將按設定時間自動發送訊息。
              </div>
              <div className="flex gap-2 mt-4">
                <button onClick={() => setShowPreview(false)} className="flex-1 px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-white rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600">返回修改</button>
                {can(PERMISSIONS.BROADCAST_SEND) && (
                  <button onClick={handleCreateTask} disabled={isSubmitting} className="flex-1 flex items-center justify-center px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50">
                    {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4 mr-1" /> 確認創建</>}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Campaign Form Modal */}
        {showCampaignForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <h2 className="text-2xl font-bold mb-4 text-gray-900 dark:text-white">{editingCampaignId ? '編輯模板' : '新增模板'}</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">模板名稱</label>
                  <input type="text" value={campaignForm.name} onChange={e => setCampaignForm(p => ({ ...p, name: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg dark:bg-gray-800 dark:text-white dark:border-gray-600" />
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">訊息版本（最多 5 個）</label>
                    <span className="text-xs text-gray-500 dark:text-gray-400">{campaignForm.templates.length} / 5</span>
                  </div>
                  <div className="space-y-3">
                    {campaignForm.templates.map((t, i) => (
                      <div key={i} className="p-3 border border-gray-200 dark:border-gray-700 rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <span className="px-2 py-0.5 bg-orange-100 text-orange-700 text-xs rounded font-medium dark:bg-orange-900/30 dark:text-orange-300">版本 {t.versionLabel || String.fromCharCode(65 + i)}</span>
                          {campaignForm.templates.length > 1 && can(PERMISSIONS.BROADCAST_SEND) && (
                            <button
                              onClick={() => setCampaignForm(p => ({ ...p, templates: p.templates.filter((_, idx) => idx !== i) }))}
                              className="text-red-600 hover:text-red-700 text-xs flex items-center"
                            >
                              <X className="w-3 h-3 mr-0.5" /> 刪除
                            </button>
                          )}
                        </div>
                        <textarea
                          rows={4}
                          value={t.messageContent}
                          onChange={e => {
                            const next = [...campaignForm.templates];
                            next[i] = { ...next[i], messageContent: e.target.value };
                            setCampaignForm(p => ({ ...p, templates: next }));
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg dark:bg-gray-800 dark:text-white dark:border-gray-600"
                          placeholder="輸入訊息內容..."
                        />
                      </div>
                    ))}
                  </div>
                  {campaignForm.templates.length < 5 && can(PERMISSIONS.BROADCAST_SEND) && (
                    <button
                      onClick={() => setCampaignForm(p => ({ ...p, templates: [...p.templates, { versionLabel: String.fromCharCode(65 + p.templates.length), messageContent: '' }] }))}
                      className="mt-3 flex items-center px-3 py-1.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 text-sm"
                    >
                      <Plus className="w-4 h-4 mr-1" /> 添加版本
                    </button>
                  )}
                  <p className="text-xs text-orange-600 dark:text-orange-400 mt-2">可用變量: {'{{name}}'} = 客戶名稱。發送時會隨機選擇一個版本。</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">媒體圖片 URL（可選）</label>
                  <input type="text" value={campaignForm.mediaUrl} onChange={e => setCampaignForm(p => ({ ...p, mediaUrl: e.target.value }))} placeholder="https://example.com/image.jpg" className="w-full px-3 py-2 border border-gray-300 rounded-lg dark:bg-gray-800 dark:text-white dark:border-gray-600" />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">必須是直連圖片連結，支援 image / video / audio / document</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">媒體類型</label>
                  <select value={campaignForm.mediaType} onChange={e => setCampaignForm(p => ({ ...p, mediaType: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg dark:bg-gray-800 dark:text-white dark:border-gray-600">
                    <option value="">純文字（無媒體）</option>
                    <option value="image">圖片 (image)</option>
                    <option value="video">影片 (video)</option>
                    <option value="audio">音訊 (audio)</option>
                    <option value="document">文件 (document)</option>
                  </select>
                </div>
              </div>
              <div className="flex gap-2 mt-6">
                <button onClick={() => setShowCampaignForm(false)} className="flex-1 px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-white rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600">取消</button>
                {can(PERMISSIONS.BROADCAST_SEND) && (
                  <button
                    onClick={handleSaveCampaign}
                    disabled={isSavingCampaign}
                    className="flex-1 flex items-center justify-center px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSavingCampaign ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
                    儲存
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Preview Campaign Modal */}
        {previewCampaign && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full relative max-h-[90vh] overflow-y-auto">
              <h2 className="text-2xl font-bold mb-4 text-gray-900 dark:text-white">模板預覽</h2>
              <button onClick={() => setPreviewCampaign(null)} className="absolute top-4 right-4 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"><X className="w-5 h-5" /></button>
              <div className="space-y-3 max-h-80 overflow-y-auto">
                {(previewCampaign.templates || [{ versionLabel: 'A', messageContent: previewCampaign.messageContent }]).map((t, i) => (
                  <div key={i} className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4 text-sm text-gray-800 dark:text-white whitespace-pre-wrap break-words">
                    <span className="inline-block px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded font-medium mb-2 dark:bg-green-900/30 dark:text-green-300">版本 {t.versionLabel || String.fromCharCode(65 + i)}</span>
                    <div>{replaceVars(t.messageContent, { name: '陳大文' })}</div>
                  </div>
                ))}
              </div>
              <button onClick={() => setPreviewCampaign(null)} className="w-full mt-4 px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-white rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600">關閉</button>
            </div>
          </div>
        )}

      </div>
    </AdminLayout>
  );
};

export default AdminBroadcast;
